
import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import IconButton from 'components/partials/button/icon-button';
import { Select } from 'components/partials/select/select';
import DatePicker from 'components/partials/date-picker/date-picker';
import { parseTypedDate } from 'components/partials/date-picker/helpers/date-format';

import CeebCodesField from './ceeb-codes-field';
import EligibilityValueField from './eligibility-value-field';
import type { CeebCodesUpdater } from '../hooks/use-ceeb-codes-input';

import {
  ELIGIBILITY_CURRENCY_CRITERIA,
  ELIGIBILITY_RANGE_ONLY_CRITERIA,
} from '../calculations-step.constants';
import type { DictionaryDrivenOption } from '../helpers/build-dictionary-options';

import Icons from 'assets/icons';

import type { CeebCodeChip } from '../calculations-step.types';
import {
  EligibilityCriteriaDtoCriteria,
  EligibilityCriteriaDtoOperand,
} from 'api/generated/schemas';

/** The eligibility fields of one criteria block, keyed by `rowId` for this component. */
export interface EligibilityRow {
  rowId: string;
  criteria: EligibilityCriteriaDtoCriteria | '';
  operand: EligibilityCriteriaDtoOperand | '';
  amount: string;
  amountFrom: string;
  amountTo: string;
  /** App Date only — `mm.dd.yyyy` display strings. */
  dateFrom: string;
  dateTo: string;
  ceebCodes: CeebCodeChip[];
}

const DISTANCE_RANGE_ERROR = 'To must be at least From';
const APP_DATE_RANGE_ERROR = 'To must be on or after From';

/** FR-5a: To must be >= From once both Distance bounds are typed. */
const getDistanceToError = (
  amountFrom: string,
  amountTo: string
): string | undefined => {
  if (!amountFrom || !amountTo) {
    return undefined;
  }

  return Number(amountFrom) > Number(amountTo)
    ? DISTANCE_RANGE_ERROR
    : undefined;
};

/** FR-11a: To must be on/after From once both App Date bounds are well-formed dates. */
const getAppDateToError = (
  dateFrom: string,
  dateTo: string
): string | undefined => {
  const from = parseTypedDate(dateFrom);
  const to = parseTypedDate(dateTo);

  if (!from || !to) {
    return undefined;
  }

  return to.isBefore(from) ? APP_DATE_RANGE_ERROR : undefined;
};

const MENU_STYLES = (): Record<string, unknown> => ({
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
  }),
});

export interface SpecialAwardEligibilityRowProps {
  row: EligibilityRow;
  criteriaOptions: DictionaryDrivenOption<EligibilityCriteriaDtoCriteria>[];
  operandOptions: DictionaryDrivenOption<EligibilityCriteriaDtoOperand>[];
  /** FR-15a: only shown once a second row exists. */
  showDelete: boolean;
  onCriteriaChange: (
    rowId: string,
    criteria: EligibilityCriteriaDtoCriteria
  ) => void;
  onOperandChange: (
    rowId: string,
    operand: EligibilityCriteriaDtoOperand
  ) => void;
  onValueChange: (
    rowId: string,
    field: 'amount' | 'amountFrom' | 'amountTo' | 'dateFrom' | 'dateTo',
    value: string
  ) => void;
  onCeebCodesChange: (rowId: string, updater: CeebCodesUpdater) => void;
  onRemove?: (rowId: string) => void;
}

const SpecialAwardEligibilityRow = ({
  row,
  criteriaOptions,
  operandOptions,
  showDelete,
  onCriteriaChange,
  onOperandChange,
  onValueChange,
  onCeebCodesChange,
  onRemove,
}: SpecialAwardEligibilityRowProps) => {
  const isCeeb = row.criteria === EligibilityCriteriaDtoCriteria.CEEB;
  const isDistance = row.criteria === EligibilityCriteriaDtoCriteria.DISTANCE;
  const isRangeOnly =
    !!row.criteria && ELIGIBILITY_RANGE_ONLY_CRITERIA.has(row.criteria);
  const isBetween = row.operand === EligibilityCriteriaDtoOperand.BETWEEN;
  const isCurrency =
    !!row.criteria && ELIGIBILITY_CURRENCY_CRITERIA.has(row.criteria);

  const selectedCriteria =
    criteriaOptions.find((option) => option.value === row.criteria) ?? null;
  const selectedOperand =
    operandOptions.find((option) => option.value === row.operand) ?? null;

  const dimensionSelect = (
    <Select<DictionaryDrivenOption<EligibilityCriteriaDtoCriteria>>
      label="Dimension"
      labelInTop
      required
      options={criteriaOptions}
      value={selectedCriteria ?? undefined}
      onChange={(option) => {
        if (option && !Array.isArray(option)) {
          onCriteriaChange(row.rowId, option.value);
        }
      }}
      placeholder="Select..."
      menuPortalTarget={document.body}
      extraStyles={MENU_STYLES}
      customClass="mt-0 text-md"
      extraBasicStyles={{ minHeight: 40, padding: 0 }}
      data-testid={`eligibility-dimension-${row.rowId}`}
    />
  );

  const renderRemoveButton = (className?: string) =>
    showDelete && (
      <IconButton
        icon={<Icons.Delete />}
        variant="reserve-text"
        size="md"
        aria-label="Remove eligibility criteria"
        onClick={() => onRemove?.(row.rowId)}
        className={className}
        data-testid={`eligibility-remove-${row.rowId}`}
      />
    );

  const renderDistanceFields = () => (
    <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ width: '181px', flexShrink: 0 }}>
        <EligibilityValueField
          label="From"
          labelFor={`eligibility-amount-from-${row.rowId}`}
          value={row.amountFrom}
          isCurrency={false}
          unitSuffix="miles"
          onChange={(value) => onValueChange(row.rowId, 'amountFrom', value)}
        />
      </Box>
      <Box sx={{ width: '181px', flexShrink: 0 }}>
        <EligibilityValueField
          label="To"
          labelFor={`eligibility-amount-to-${row.rowId}`}
          value={row.amountTo}
          isCurrency={false}
          unitSuffix="miles"
          errorMessage={getDistanceToError(row.amountFrom, row.amountTo)}
          onChange={(value) => onValueChange(row.rowId, 'amountTo', value)}
        />
      </Box>
    </Stack>
  );

  const renderAppDateFields = () => (
    <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ width: '181px', flexShrink: 0 }}>
        <DatePicker
          label="From"
          labelFor={`eligibility-date-from-${row.rowId}`}
          required
          value={row.dateFrom}
          customClass="text-md mt-0"
          inputCustomClass="h-[38px]"
          onChange={(value) => onValueChange(row.rowId, 'dateFrom', value)}
        />
      </Box>
      <Box sx={{ width: '181px', flexShrink: 0 }}>
        <DatePicker
          label="To"
          labelFor={`eligibility-date-to-${row.rowId}`}
          required
          value={row.dateTo}
          errorMessage={getAppDateToError(row.dateFrom, row.dateTo)}
          customClass="text-md mt-0"
          inputCustomClass="h-[38px]"
          onChange={(value) => onValueChange(row.rowId, 'dateTo', value)}
        />
      </Box>
    </Stack>
  );

  const renderNonCeebFields = () => {
    if (!row.criteria) {
      return null;
    }

    if (isRangeOnly) {
      return isDistance ? renderDistanceFields() : renderAppDateFields();
    }

    return (
      <>
        <Box sx={{ minWidth: 0, flex: '1 1 0%' }}>
          <Select<DictionaryDrivenOption<EligibilityCriteriaDtoOperand>>
            label="Condition"
            labelInTop
            required
            options={operandOptions}
            value={selectedOperand ?? undefined}
            onChange={(option) => {
              if (option && !Array.isArray(option)) {
                onOperandChange(row.rowId, option.value);
              }
            }}
            placeholder="Select..."
            menuPortalTarget={document.body}
            extraStyles={MENU_STYLES}
            customClass="mt-0 text-md"
            extraBasicStyles={{ minHeight: 40, padding: 0 }}
            data-testid={`eligibility-condition-${row.rowId}`}
          />
        </Box>

        {isBetween && (
          <Stack direction="row" sx={{ alignItems: 'flex-end', gap: 1.5 }}>
            <Box sx={{ width: '124px', flexShrink: 0 }}>
              <EligibilityValueField
                label="Minimum value"
                labelFor={`eligibility-amount-from-${row.rowId}`}
                value={row.amountFrom}
                isCurrency={isCurrency}
                onChange={(value) =>
                  onValueChange(row.rowId, 'amountFrom', value)
                }
              />
            </Box>
            <span className="pb-2.5 text-sm text-gray-700">and</span>
            <Box sx={{ width: '124px', flexShrink: 0 }}>
              <EligibilityValueField
                label="Maximum value"
                labelFor={`eligibility-amount-to-${row.rowId}`}
                value={row.amountTo}
                isCurrency={isCurrency}
                onChange={(value) =>
                  onValueChange(row.rowId, 'amountTo', value)
                }
              />
            </Box>
          </Stack>
        )}

        {!!row.operand && !isBetween && (
          <Box sx={{ minWidth: 0, flex: '1 1 0%' }}>
            <EligibilityValueField
              label="Value"
              labelFor={`eligibility-amount-${row.rowId}`}
              value={row.amount}
              isCurrency={isCurrency}
              onChange={(value) => onValueChange(row.rowId, 'amount', value)}
            />
          </Box>
        )}
      </>
    );
  };

  return (
    <Stack
      direction={isCeeb ? 'column' : 'row'}
      sx={{ width: '100%', alignItems: 'flex-start', gap: 1.5, pr: '54px' }}
      data-testid={`eligibility-row-${row.rowId}`}
    >
      {isCeeb ? (
        <Stack
          direction="row"
          sx={{ width: '100%', alignItems: 'flex-end', gap: 1.5 }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 0%' }}>{dimensionSelect}</Box>
          {renderRemoveButton()}
        </Stack>
      ) : (
        <Box sx={{ width: '10rem', flexShrink: 0 }}>{dimensionSelect}</Box>
      )}

      {isCeeb ? (
        <Box sx={{ width: '100%' }}>
          <CeebCodesField
            labelFor={`eligibility-ceeb-${row.rowId}`}
            codes={row.ceebCodes}
            onCodesChange={(updater) => onCeebCodesChange(row.rowId, updater)}
            data-testid={`eligibility-ceeb-${row.rowId}`}
          />
        </Box>
      ) : (
        renderNonCeebFields()
      )}

      {!isCeeb && renderRemoveButton('self-end ml-auto')}
    </Stack>
  );
};

export default SpecialAwardEligibilityRow;
